import { ExpandLess, ExpandMore, Tune as Settings2Icon } from '@mui/icons-material';
import { Box, Button, ButtonBase, Checkbox, Collapse, Divider, Popover, Stack, Typography } from '@mui/material';
import { useState, MouseEvent } from 'react';
import ReactGA from 'react-ga4';
import { OrderAttrFilter } from 'src/components/dispatch/orderAttrFilter';
import OrderAttrFilterSection from 'src/components/dispatch/OrderAttrFilterSection';
import {
  BaseMetric,
  buildDisplayMetricGroups,
  CARD_METRIC_LIMIT,
  cardMetrics,
  DisplayMetric,
  DEFAULT_VEHICLE_METRICS,
  checkedDisplayMetrics,
  displayMetricLabel,
  isPlaceMetric,
  resolvePlaceMetrics,
  isVehicleMetric,
  isValueMetric,
  toCardMetricKey,
  VALUE_METRICS,
  VEHICLE_METRICS,
  visibleDisplayMetrics,
} from 'src/components/dispatch/unassignedDisplay/displayMetricKeys';

// 既存 14 ファイルがこのモジュールから型を import しているため re-export で互換を保つ。
export type { ValueMetric, VehicleMetric, InfoMetric, DisplayMetric } from 'src/components/dispatch/unassignedDisplay/displayMetricKeys';

const sectionLabelSx = {
  fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary',
} as const;

/**
 * セクション見出し。note はその設定がどのリストに効くかの注記。
 * 数値指標だけが車両リストにも効くため、注記が無いと表示情報との違いが伝わらない。
 */
const sectionHeader = (label: string, note?: string) => (
  <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
    <Typography variant="caption" sx={sectionLabelSx} display="block">{label}</Typography>
    {note && (
      <Typography variant="caption" sx={{ fontSize: 10, color: 'text.disabled' }} display="block">
        {note}
      </Typography>
    )}
  </Box>
);

type ToggleGroupLabel = '数値指標 切替' | '車両リスト 切替' | '表示情報 切替';

interface Props {
  value: DisplayMetric[];
  onChange: (metrics: DisplayMetric[]) => void;
  /** license.custom_input_fields。license 未取得のあいだは空配列として扱う */
  customInputFieldNames: string[];
  isLicenseReady: boolean;
  /** GA の screen_name。/map と convenience で異なるため呼び出し側から渡す */
  screenName: string;
  /** 表示対象（配送形態・区間種別）の絞り込み。非表示にしている値を持つ */
  orderAttrFilter: OrderAttrFilter;
  onOrderAttrFilterChange: (next: OrderAttrFilter) => void;
  /**
   * カード表示（配車カードのセルに最大 3 件）のセクションを出すか。
   * 配車カードを持つカード型配車画面だけ true。/map には配車カードが無い（issue #316）。
   */
  showCardMetrics?: boolean;
}

const DisplayMetricToggle = ({
  value, onChange, customInputFieldNames, isLicenseReady, screenName, orderAttrFilter, onOrderAttrFilterChange,
  showCardMetrics = false,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  // チェック状態は実効集合（checkedDisplayMetrics）で判定する。数値指標が保存値に
  // 1 つも無いとき、VehicleInfoView は quantity にフォールバックして容量を描くため、
  // チェック状態もそれに合わせて quantity を有効として扱わないと表示と食い違う。
  // 積地・降地も同じ理由で、両方外れているときは既定表示の降地を有効として扱う。
  const checked = checkedDisplayMetrics(value);
  const valueSet = new Set(checked);

  // license 未取得のあいだは任意項目を「無い」と確定させない（描かないだけ）。
  const availableCustomNames = isLicenseReady ? customInputFieldNames : [];
  const groups = buildDisplayMetricGroups(availableCustomNames);
  // バッジと全解除防止は「実効集合 かつ 可視集合」基準。保存値には他社の custom: キーが
  // 残る一方、数値指標ゼロの保存値では quantity が実効的に有効になる。チェック状態
  // (valueSet、上記) と同じ effectiveDisplayMetrics を通すことでバッジ件数とチェック数を
  // 一致させる（通さないと保存値 ['grossProfit'] のときチェックは2件なのにバッジが1件になる）。
  const activeCount = visibleDisplayMetrics(checked, availableCustomNames).length;

  // カード表示のチップ。並びは数値指標 → 表示情報のカテゴリ定義順で固定する
  // （保存順に並べると、チェックを付け直すたびにチップの位置が動いて押し間違える）。
  const chipMetrics: BaseMetric[] = [
    ...VALUE_METRICS.filter((m) => valueSet.has(m.key)).map((m) => m.key),
    ...groups.flatMap((group) => group.items).filter((item) => valueSet.has(item.key)).map((item) => item.key),
  ];
  const activeCardMetrics = cardMetrics(value);

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => ({ location: true }));
  const allGroupsOpen = groups.every((group) => openGroups[group.id]);

  /** 生の保存値に「既定で ON に見えているキー」を書き出す。外す操作の前段でだけ必要 */
  const confirmDefaults = (metric: DisplayMetric): DisplayMetric[] => {
    // 車両タグは未保存のとき既定で ON になっているため、生の保存値から filter しても
    // 消えない。そのタグを押すときだけ、既定を保存値に確定させてから外す。
    const withVehicleDefaults = isVehicleMetric(metric) && !value.some(isVehicleMetric)
      ? [...value, ...DEFAULT_VEHICLE_METRICS]
      : value;
    // 積地・降地も同じ扱い。両方外れているときは降地が既定表示でチェックが付いて
    // 見えるので、確定させずに積地を足すと降地まで消える（実機で確認）。
    const places = resolvePlaceMetrics(value);
    return isPlaceMetric(metric) && places.destination && !value.includes('destination')
      ? [...withVehicleDefaults, 'destination']
      : withVehicleDefaults;
  };

  const toggle = (metric: DisplayMetric, groupLabel: ToggleGroupLabel) => {
    const nextChecked = !valueSet.has(metric);
    ReactGA.event('change', {
      screen_name: screenName,
      button_name: groupLabel,
      label: `${metric} ${nextChecked ? 'ON' : 'OFF'}`,
    });
    // 編集は生の保存値へ（他社の custom: キーを保持する）。
    const base = confirmDefaults(metric);
    // 基準項目を外したらカード表示の指定も一緒に落とす。残すと「表示項目に無い項目が
    // 配車カードのセルに出続ける」状態になり、外す手段が画面から無くなる。
    const cardKey = toCardMetricKey(metric as BaseMetric);
    const nextRaw = nextChecked
      ? [...base, metric]
      : base.filter((m) => m !== metric && m !== cardKey);
    // 判定は実効集合へ（チェック状態と同じ effectiveDisplayMetrics を通す）。生の
    // nextRaw だけで判定すると、保存値が数値指標ゼロのレガシー値 (['grossProfit'] 等)
    // のときチェック表示は quantity を含めて 2 件に見えるのに、生の可視件数だけで
    // 判定すると nextRaw=[] が可視 0 件と誤判定されて粗利も外せない行き止まりになる
    // (実測で発生した不具合)。
    if (visibleDisplayMetrics(checkedDisplayMetrics(nextRaw), availableCustomNames).length === 0) return;
    // 数値指標 (quantity/weight/volume) は契約上常に最低 1 つ必要（車両カードが常に
    // 容量とゲージを描画するため）。OFF にした結果ゼロになるなら弾く。粗利や表示情報
    // キーの OFF はこの契約の対象外 (isValueMetric で数値指標だけに絞る)。
    if (isValueMetric(metric) && !nextChecked && !nextRaw.some(isValueMetric)) return;
    onChange(nextRaw);
  };

  /**
   * カテゴリ単位の一括 ON/OFF。カテゴリが 6 つに増えて 1 項目ずつ押す手間が増えたため。
   * 全解除で可視 0 件になる保存値は弾く（単体トグルと同じ全解除防止）。
   */
  const toggleGroupAll = (groupId: string, keys: BaseMetric[], allActive: boolean) => {
    ReactGA.event('change', {
      screen_name: screenName,
      button_name: '表示情報 一括切替',
      label: `${groupId} ${allActive ? 'OFF' : 'ON'}`,
    });
    // 単体トグルと同じく、外すときはカード表示の指定も一緒に落とす
    const dropped = new Set<DisplayMetric>([...keys, ...keys.map(toCardMetricKey)]);
    const nextRaw = allActive
      ? value.filter((m) => !dropped.has(m))
      : [...value, ...keys.filter((key) => !value.includes(key))];
    if (visibleDisplayMetrics(checkedDisplayMetrics(nextRaw), availableCustomNames).length === 0) return;
    onChange(nextRaw);
  };

  /**
   * カード表示の指定。上限に達しているときは最も古い指定を押し出す。
   * 上限で弾くのではなく押し出すのは、3 件埋まった状態から選び直すのに
   * 「まず外す」操作を挟ませないため（サンプル route-planner MetricToggle 準拠）。
   */
  const toggleCardMetric = (metric: BaseMetric) => {
    const key = toCardMetricKey(metric);
    const isOn = value.includes(key);
    ReactGA.event('change', {
      screen_name: screenName,
      button_name: 'カード表示 切替',
      label: `${metric} ${isOn ? 'OFF' : 'ON'}`,
    });
    if (isOn) {
      onChange(value.filter((m) => m !== key));
      return;
    }
    const appended = [...value, key];
    const keys = cardMetrics(appended);
    const next = keys.length > CARD_METRIC_LIMIT
      ? appended.filter((m) => m !== toCardMetricKey(keys[0]))
      : appended;
    onChange(next);
  };

  const handleOpen = (e: MouseEvent<HTMLButtonElement>) => {
    ReactGA.event('click', { screen_name: screenName, button_name: '表示項目' });
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const checkboxRow = (key: DisplayMetric, label: string, groupLabel: ToggleGroupLabel) => (
    <Stack
      key={key}
      direction="row"
      alignItems="center"
      spacing={0.5}
      component="label"
      sx={{
        px: 0.75, py: 0.5, borderRadius: 1, cursor: 'pointer', flex: '0 0 auto',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Checkbox
        size="small"
        checked={valueSet.has(key)}
        onChange={() => toggle(key, groupLabel)}
        inputProps={{ 'aria-label': label }}
        sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: 14 } }}
      />
      <Typography variant="caption" sx={{ fontSize: 12, fontWeight: 500, color: 'text.primary', whiteSpace: 'nowrap' }}>
        {label}
      </Typography>
    </Stack>
  );

  return (
    <>
      <Button
        size="small"
        onClick={handleOpen}
        startIcon={<Settings2Icon sx={{ fontSize: 14 }} />}
        sx={{
          height: 28,
          minWidth: 0,
          px: 1,
          fontSize: 10,
          fontWeight: 600,
          textTransform: 'none',
          color: 'text.secondary',
          gap: 0.5,
          '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
        }}
      >
        表示項目
        {activeCount > 0 && (
          <Box
            component="span"
            sx={{
              ml: 0.25,
              px: 0.75,
              py: 0.25,
              borderRadius: 999,
              bgcolor: 'hsl(var(--primary) / 0.15)',
              color: 'primary.main',
              fontSize: 9,
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            {activeCount}
          </Box>
        )}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        // 幅 320: カテゴリが 6 つ・項目が 24 に増えたので 1 列だと縦に長くなりすぎる。
        // 高さは 72vh で頭打ちにして、画面下にはみ出さないようにする。
        slotProps={{ paper: { sx: { width: 320, p: 0, maxHeight: '72vh' } } }}
      >
        {/* カード表示（配車カードのセル）。表示情報より先に置くのは、選べる項目が
            下のチェックの選択結果に依存する＝上から順に読むと関係が分かるため */}
        {showCardMetrics && (
          <>
            <Stack direction="row" alignItems="baseline" sx={{ px: 1.5, pt: 1.5, pb: 0.5, gap: 0.75 }}>
              <Typography variant="caption" sx={sectionLabelSx}>カード表示</Typography>
              <Typography variant="caption" sx={{ flex: 1, fontSize: 10, color: 'text.disabled' }}>
                配車カードのセルに表示（最大{CARD_METRIC_LIMIT}件）
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: activeCardMetrics.length >= CARD_METRIC_LIMIT ? 'primary.main' : 'text.secondary',
                }}
              >
                {activeCardMetrics.length}/{CARD_METRIC_LIMIT}
              </Typography>
            </Stack>
            <Box sx={{ px: 1.5, pb: 1.25, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {chipMetrics.length === 0 && (
                <Typography variant="caption" sx={{ fontSize: 10, color: 'text.disabled' }}>
                  下の項目を選択すると、ここでカードに表示する項目を選べます
                </Typography>
              )}
              {chipMetrics.map((metric) => {
                const order = activeCardMetrics.indexOf(metric);
                const active = order >= 0;
                return (
                  <Box
                    key={metric}
                    component="button"
                    type="button"
                    onClick={() => toggleCardMetric(metric)}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 0.75,
                      py: 0.25,
                      borderRadius: 999,
                      cursor: 'pointer',
                      fontSize: 11,
                      fontWeight: 600,
                      lineHeight: 1.6,
                      border: '1px solid',
                      borderColor: active ? 'hsl(var(--primary) / 0.6)' : 'hsl(var(--border))',
                      bgcolor: active ? 'hsl(var(--primary) / 0.12)' : 'transparent',
                      color: active ? 'primary.main' : 'text.secondary',
                      '&:hover': { bgcolor: active ? 'hsl(var(--primary) / 0.18)' : 'action.hover' },
                    }}
                  >
                    {active && (
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 13,
                          height: 13,
                          borderRadius: 999,
                          bgcolor: 'primary.main',
                          color: 'hsl(var(--primary-foreground))',
                          fontSize: 9,
                          fontWeight: 700,
                        }}
                      >
                        {order + 1}
                      </Box>
                    )}
                    {displayMetricLabel(metric)}
                  </Box>
                );
              })}
            </Box>
            <Divider />
          </>
        )}

        {sectionHeader('数値指標', '車両リスト・未割当リストに反映')}
        {/* 3 列グリッド。1 列だと 3 項目で縦を使いすぎる（サンプル準拠） */}
        <Box sx={{ px: 1, pb: 1, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 0.25 }}>
          {VALUE_METRICS.map(({ key, label }) => checkboxRow(key, label, '数値指標 切替'))}
        </Box>

        <Divider />

        {/* 6 カテゴリを束ねる見出し。どこに効く設定なのかが分からないという指摘への対応 */}
        <Stack direction="row" alignItems="flex-start" sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" sx={sectionLabelSx} display="block">表示情報</Typography>
            <Typography variant="caption" sx={{ fontSize: 10, color: 'text.disabled' }} display="block">
              未割当リストに反映
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={() => setOpenGroups(
              allGroupsOpen ? {} : Object.fromEntries(groups.map((group) => [group.id, true])),
            )}
            sx={{ minWidth: 0, px: 0.75, fontSize: 10, fontWeight: 600, textTransform: 'none', color: 'text.secondary' }}
          >
            {allGroupsOpen ? 'すべて閉じる' : 'すべて開く'}
          </Button>
        </Stack>

        {/*
          カテゴリヘッダは ButtonBase（= ネイティブ button）。onClick だけの Stack だと
          キーボードで開閉できない。一括選択は入れ子の button にせず兄弟に置く
          （button の入れ子は不正な DOM で、クリックの伝播も読みにくくなる）。
        */}
        {groups.map((group) => {
          const keys = group.items.map((item) => item.key);
          const activeInGroup = group.items.filter((i) => valueSet.has(i.key)).length;
          const allActive = activeInGroup === group.items.length;
          return (
            <Box key={group.id}>
              <Stack direction="row" alignItems="center" sx={{ pr: 1 }}>
                <ButtonBase
                  onClick={() => setOpenGroups((prev) => ({ ...prev, [group.id]: !prev[group.id] }))}
                  aria-expanded={!!openGroups[group.id]}
                  sx={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    px: 1.5, py: 0.75, textAlign: 'left', '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Typography variant="caption" sx={sectionLabelSx}>{group.label}</Typography>
                    {activeInGroup > 0 && (
                      <Box
                        sx={{
                          px: 0.5, py: 0.25, borderRadius: 999, bgcolor: 'hsl(var(--primary) / 0.15)',
                          color: 'primary.main', fontSize: 9, fontWeight: 700, lineHeight: 1,
                        }}
                      >
                        {activeInGroup}
                      </Box>
                    )}
                  </Stack>
                  {openGroups[group.id] ? <ExpandLess sx={{ fontSize: 14 }} /> : <ExpandMore sx={{ fontSize: 14 }} />}
                </ButtonBase>
                <Button
                  size="small"
                  onClick={() => toggleGroupAll(group.id, keys, allActive)}
                  sx={{ minWidth: 0, px: 0.5, fontSize: 10, fontWeight: 600, textTransform: 'none', color: 'text.secondary' }}
                >
                  {allActive ? '全解除' : '全選'}
                </Button>
              </Stack>
              <Collapse in={openGroups[group.id]}>
                {/* 項目名の長さがまちまちなので折り返しで詰める（固定列だと空きが目立つ） */}
                <Box sx={{ px: 1, pb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
                  {group.items.map(({ key, label }) => checkboxRow(key, label, '表示情報 切替'))}
                </Box>
              </Collapse>
            </Box>
          );
        })}

        <Divider />

        {/* 案件と車両の絞り込み。表示情報（見せ方）とは効き方が違うので独立したセクションにする。 */}
        <OrderAttrFilterSection
          value={orderAttrFilter}
          onChange={(next, changed) => {
            ReactGA.event('change', {
              screen_name: screenName,
              button_name: '表示対象 切替',
              label: `${changed.label} ${changed.visible ? 'ON' : 'OFF'}`,
            });
            onOrderAttrFilterChange(next);
          }}
        />

        <Divider />

        {/* 車両リスト専用の指標。カード側には出ないので表示情報のアコーディオンとは分ける。 */}
        {sectionHeader('車両リスト表示タグ')}
        <Stack sx={{ px: 1, pb: 1 }} spacing={0.25}>
          {VEHICLE_METRICS.map(({ key, label }) => checkboxRow(key, label, '車両リスト 切替'))}
        </Stack>
      </Popover>
    </>
  );
};

export default DisplayMetricToggle;
